/**
 * FreeBuilder — a bridge-builder-style, free-form BUILD MODE.
 *
 * A self-contained editing module. The user draws a 2D truss in a vertical
 * X–Y plane at z=0 (place nodes, drag out members, move/select/copy-paste/
 * delete). Whenever the graph changes it emits a *3D-stable* Structure via
 * `onChange` (debounced), and `getStructure()` returns the same on demand.
 *
 * ── Why extrude to 3D? ────────────────────────────────────────────────────
 * The solver in `@/physics/solver` is fully 3D. A purely planar truss (all
 * nodes at z=0) has zero out-of-plane stiffness, so it is a mechanism about
 * the z-axis and would fall over sideways the instant it is simulated. To make
 * the built graph a *real* thin lattice that stands, `getStructure()` extrudes
 * the authored 2D graph to a shallow depth `d` (default 1.5 m):
 *   • every 2D node is mirrored to z = +d/2 (front) and z = −d/2 (back);
 *   • every authored 2D member becomes TWO members (front + back copy);
 *   • the two planes are tied together at each node by a transverse "batten"
 *     member, and each authored member gets an X-brace (the two diagonals of
 *     the rectangular panel it forms with its mirror) so the box is triangulated
 *     and rigid out-of-plane.
 * Ground-row nodes (authored y ≈ 0) become fixed anchors on both planes.
 *
 * The module owns its own preview geometry inside the given `scene`; it never
 * touches the app's tower render. Raycasting a z=0 plane places nodes; raycasting
 * the node spheres selects/moves them.
 *
 * ── Wiring (maintainer) ───────────────────────────────────────────────────
 *   const fb = new FreeBuilder({
 *     scene, getCamera: () => viewport.getActiveCamera(),
 *     domElement: renderer.domElement, container: someHtmlOverlayDiv,
 *     onChange: (s) => { solver = new Solver(s); },   // re-sim live
 *   });
 *   fb.enable();                       // start editing (call disable() to stop)
 *   const s = fb.getStructure();       // extruded, mass-computed, sim-ready
 *   fb.clear(); fb.dispose();
 *
 * Feed `onChange(structure)` (or `getStructure()`) straight into
 * `new Solver(structure)` — the structure is already extruded and has
 * `computeMasses` applied, so it simulates directly.
 */

import * as THREE from "three";
import {
  type Member,
  type Structure,
  type Section,
  type SectionKey,
  type MemberOptions,
  SECTIONS,
  GUY_STRAND,
  makeNode,
  addMember,
  computeMasses,
  MATERIALS,
  type MaterialKey,
} from "@/physics/model";
import type { FreeGraph } from "@/game/towerSpec";

// ── options ────────────────────────────────────────────────────────────────

export interface FreeBuildOpts {
  scene: THREE.Scene;
  getCamera: () => THREE.Camera;
  getFaces?: () => number;
  setFaces?: (faces: number) => void;
  /** Element that receives pointer events (usually the renderer canvas). */
  domElement: HTMLElement;
  /** Element the toolbar UI is appended into (an HTML overlay over the canvas). */
  container: HTMLElement;
  /** Fired (debounced) whenever the built graph changes, with a sim-ready Structure. */
  onChange: (s: Structure) => void;
}

/** A floating copy-paste part being placed: transformed member segments + nodes. */
export interface PlacementGhost {
  members: Array<{ x1: number; y1: number; x2: number; y2: number; section: SectionKey }>;
  nodes: Array<{ x: number; y: number }>;
  scale: number;
  angleDeg: number;
}

export interface FreeBuildOverlay {
  nodes: Array<{ id: number; x: number; y: number; selected: boolean; fixed: boolean }>;
  members: Array<{ id: number; a: number; b: number; section: SectionKey; material: MaterialKey; selected: boolean }>;
  mirror: { enabled: boolean; axisX: number };
  /** Non-null while a pasted part is floating, ready to place (see paste()). */
  placement?: PlacementGhost | null;
}

// ── authored (2D) graph ──────────────────────────────────────────────────────

interface GNode {
  id: number;
  x: number;
  y: number;
}

interface GMember {
  id: number;
  a: number; // GNode id
  b: number; // GNode id
  section: SectionKey;
  material?: MaterialKey; // per-member steel grade / metal / etc. (default by section)
}

interface GraphState {
  nodes: GNode[];
  members: GMember[];
  selNodes: number[];
  selMembers: number[];
  idc: number;
}

/** A palette entry the user can pick to draw with. */
interface PaletteItem {
  key: SectionKey;
  label: string;
  color: number;
  /** guy → tension-only cable (drawn thin, dashed feel). */
  guy: boolean;
}

const PALETTE: PaletteItem[] = [
  { key: "legMed", label: "Leg (pipe)", color: 0x6fa8dc, guy: false },
  { key: "braceMed", label: "Brace (angle)", color: 0x93c47d, guy: false },
  { key: "rod24", label: "Rod", color: 0xffd966, guy: false },
  { key: "guy14", label: "Guy (cable)", color: 0xe06666, guy: true },
];

// ── tunables ─────────────────────────────────────────────────────────────────

const GRID = 1; // snap grid (m)
const DRAW_FALLBACK_GRID = 0.1; // finer snap used only when a draw endpoint is created from raw cursor input
const DRAW_FORCE_NEW_MIN = 0.25; // minimum drag distance to force a distinct end node
const DRAW_AXIS_PRESERVE_MIN = 0.08; // if drag clearly moved on an axis, avoid snapping both endpoints onto the same axis value
const DRAW_CENTERLINE_INTENT_X = 0.06; // near-axis drag is treated as an intentional centerline stroke in mirror mode
const DRAW_CENTERLINE_ANCHOR_X = 0.12; // near-axis endpoint becomes a centerline anchor when the opposite endpoint is clearly off-axis
const GROUND_EPS = 1e-3; // |y| below this ⇒ fixed ground anchor
const FALLBACK_DEPTH = 1.5; // fallback extrusion depth (m) when a row has no width
const NODE_R = 0.35; // preview node sphere radius (m)
const PICK_R = 0.9; // node pick radius (m)
const MEMBER_PICK_R = 1.5; // component pick radius in elevation view (m)
const DEBOUNCE_MS = 120;
const PLACE_CENTERLINE_SNAP = 0.6; // snap a floating paste's centre to x=0 within this (m)
const PLACE_MATE_SNAP = 1.0; // snap a floating paste node onto an existing joint within this (m)

const C_NODE = 0x9fb2c2;
const C_ANCHOR = 0xf5a742;
const C_SEL = 0x39ff14;
const C_PREVIEW = 0xffffff;
const DEBUG_PREFIX = "[tower-build]";

// ── the module ───────────────────────────────────────────────────────────────

export class FreeBuilder {
  private opts: FreeBuildOpts;

  // authored 2D graph
  private nodes: GNode[] = [];
  private members: GMember[] = [];
  private idc = 0; // deterministic id counter (no Math.random / Date)

  // selection (sets of ids)
  private selNodes = new Set<number>();
  private selMembers = new Set<number>();

  // clipboard (copied authored subgraph, normalized to its own local origin)
  private clip: { nodes: GNode[]; members: GMember[] } | null = null;
  // Floating placement transform while a pasted part follows the cursor.
  // pos = where the part's local centre sits; scale/angle applied about that centre.
  private placement: { pos: { x: number; y: number }; localCenter: { x: number; y: number }; scale: number; angle: number } | null = null;

  // active palette family + exact section + material for newly drawn members
  private active: PaletteItem = PALETTE[0]!;
  private activeSection: SectionKey = PALETTE[0]!.key; // specific size (e.g. legXL)
  private activeMaterial: MaterialKey = "a572";

  // ── three.js preview scene objects ──
  private group = new THREE.Group();
  private nodeMesh: THREE.InstancedMesh;
  private memberGroup = new THREE.Group();
  private memberLines = new Map<number, THREE.Line>(); // member id → line
  private preview: THREE.Line; // rubber-band member preview
  private previewNode: THREE.Mesh; // ghost node under cursor
  private boxHelper: THREE.LineSegments; // box-select rectangle (in scene z=0)
  private nodeIndexToId: number[] = []; // instance index → GNode id

  // ── UI ──
  private toolbar?: HTMLElement;
  private toolbarHost?: HTMLElement;
  private toolbarFloating = true;
  private paletteButtons: HTMLButtonElement[] = [];
  private hint?: HTMLElement;
  private repeatRiseInput?: HTMLInputElement;
  private repeatScaleInput?: HTMLInputElement;
  private mirrorButton?: HTMLButtonElement;
  private materialSelect?: HTMLSelectElement;
  private sectionSelect?: HTMLSelectElement;
  private mirrorMode = true;
  private interactionMode: "edit" | "draw" = "edit";
  private modeButtons: HTMLButtonElement[] = [];
  private modeIndicator?: HTMLElement;
  private undoStack: GraphState[] = [];

  // ── interaction state ──
  private enabled = false;
  private raycaster = new THREE.Raycaster();
  private plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0); // z = 0
  private ndc = new THREE.Vector2();
  private hitPt = new THREE.Vector3();

  private mode: "idle" | "drawing" | "moving" | "boxing" | "panning" = "idle";
  private drawFromId: number | null = null; // node we started dragging a member from
  private moveStart = { x: 0, y: 0 }; // world pos where a move drag began
  private panLast = { x: 0, y: 0 }; // client px for middle-mouse view pan
  private moveOrig = new Map<number, { x: number; y: number }>(); // node id → orig pos
  private boxStart = new THREE.Vector3();
  private downPt = new THREE.Vector3(); // where pointer went down (world)
  private downOnNode: number | null = null; // node hit on pointerdown (if any)
  private downOnMember: number | null = null;
  private dragged = false; // did the pointer move past the click threshold?

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // reusable scratch
  private _dummy = new THREE.Object3D();
  private _col = new THREE.Color();

  private static readonly MAX_NODES = 4096;

  constructor(opts: FreeBuildOpts) {
    this.opts = opts;

    // node instanced mesh (small spheres)
    const sGeo = new THREE.SphereGeometry(NODE_R, 12, 8);
    const sMat = new THREE.MeshBasicMaterial({ vertexColors: true });
    this.nodeMesh = new THREE.InstancedMesh(sGeo, sMat, FreeBuilder.MAX_NODES);
    this.nodeMesh.count = 0;
    this.nodeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.nodeMesh);
    this.group.add(this.memberGroup);

    // rubber-band member preview
    const pGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(),
      new THREE.Vector3(),
    ]);
    this.preview = new THREE.Line(
      pGeo,
      new THREE.LineBasicMaterial({ color: C_PREVIEW }),
    );
    this.preview.visible = false;
    this.preview.renderOrder = 5;
    this.group.add(this.preview);

    // ghost node under cursor
    this.previewNode = new THREE.Mesh(
      new THREE.SphereGeometry(NODE_R * 0.8, 10, 6),
      new THREE.MeshBasicMaterial({
        color: C_PREVIEW,
        transparent: true,
        opacity: 0.4,
      }),
    );
    this.previewNode.visible = false;
    this.group.add(this.previewNode);

    // box-select rectangle
    this.boxHelper = new THREE.LineSegments(
      new THREE.BufferGeometry().setFromPoints(
        new Array(8).fill(0).map(() => new THREE.Vector3()),
      ),
      new THREE.LineDashedMaterial({ color: C_SEL, dashSize: 0.4, gapSize: 0.3 }),
    );
    this.boxHelper.visible = false;
    this.group.add(this.boxHelper);

    this.group.visible = false;
    this.opts.scene.add(this.group);
  }

  // ── public API ─────────────────────────────────────────────────────────────

  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.group.visible = true;
    this.buildToolbar();
    const el = this.opts.domElement;
    el.addEventListener("pointerdown", this.onPointerDown);
    el.addEventListener("pointermove", this.onPointerMove);
    el.addEventListener("pointerup", this.onPointerUp);
    el.addEventListener("wheel", this.onWheel, { passive: false });
    window.addEventListener("keydown", this.onKeyDown);
    this.redraw();
  }

  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.group.visible = false;
    this.preview.visible = false;
    this.previewNode.visible = false;
    this.boxHelper.visible = false;
    this.mode = "idle";
    const el = this.opts.domElement;
    el.removeEventListener("pointerdown", this.onPointerDown);
    el.removeEventListener("pointermove", this.onPointerMove);
    el.removeEventListener("pointerup", this.onPointerUp);
    el.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("keydown", this.onKeyDown);
    if (this.toolbar) {
      this.toolbar.remove();
      this.toolbar = undefined;
    }
  }

  /**
   * Build the sim-ready 3D Structure by extruding the authored 2D graph to a
   * shallow box (planes at z = ±DEPTH/2, tied by battens + X-braces). Masses are
   * computed so the result can be fed straight to `new Solver(structure)`.
   */
  getStructure(): Structure {
    return this.extrude();
  }

  /** A small "keycap" badge showing a tool's keyboard shortcut, pushed to the right. */
  private keycap(k: string): HTMLSpanElement {
    const s = document.createElement("span");
    s.textContent = k;
    Object.assign(s.style, {
      marginLeft: "auto",
      fontSize: "10px",
      fontWeight: "600",
      color: "#8fa3b8",
      background: "#0d1219",
      border: "1px solid #2a323c",
      borderRadius: "4px",
      padding: "0 5px",
      lineHeight: "15px",
      flex: "0 0 auto",
    } as CSSStyleDeclaration);
    return s;
  }

  mountToolbar(host: HTMLElement, floating = false): void {
    this.toolbarHost = host;
    this.toolbarFloating = floating;
    if (this.toolbar) {
      this.toolbar.remove();
      this.toolbar = undefined;
    }
    if (this.enabled) this.buildToolbar();
  }

  clear(): void {
    this.pushHistory();
    this.nodes = [];
    this.members = [];
    this.selNodes.clear();
    this.selMembers.clear();
    this.clip = null;
    this.placement = null;
    this.redraw();
    this.emitChange();
  }

  loadFromStructure(s: Structure): void {
    this.undoStack = [];
    this.nodes = [];
    this.members = [];
    this.selNodes.clear();
    this.selMembers.clear();
    this.clip = null;
    this.placement = null;
    const nodeMap = new Map<number, number>();
    const coordMap = new Map<string, number>();

    s.nodes.forEach((n, i) => {
      const x = this.snap(n.x);
      const y = Math.max(0, this.snap(n.y));
      const key = `${x.toFixed(6)},${y.toFixed(6)}`;
      let id = coordMap.get(key);
      if (id === undefined) {
        id = this.nextId();
        coordMap.set(key, id);
        this.nodes.push({ id, x, y });
      }
      nodeMap.set(i, id);
    });

    for (const m of s.members) {
      const a = nodeMap.get(m.i);
      const b = nodeMap.get(m.j);
      if (a === undefined || b === undefined || a === b) continue;
      this.addMemberGraphWithSection(a, b, sectionKeyForMember(m));
    }

    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  loadEditableElevation(s: Structure): void {
    this.undoStack = [];
    this.nodes = [];
    this.members = [];
    this.selNodes.clear();
    this.selMembers.clear();
    this.clip = null;
    this.placement = null;

    const maxZ = Math.max(...s.nodes.map((n) => n.z));
    const minZ = Math.min(...s.nodes.map((n) => n.z));
    const frontZ = Math.abs(maxZ) >= Math.abs(minZ) ? maxZ : minZ;
    const spanZ = Math.max(1e-6, maxZ - minZ);
    const frontTol = Math.max(0.05, spanZ * 0.2);
    const nodeMap = new Map<number, number>();

    s.nodes.forEach((n, i) => {
      if (Math.abs(n.z - frontZ) > frontTol) return;
      const id = this.nodeAt(this.snap(n.x), Math.max(0, this.snap(n.y)));
      nodeMap.set(i, id);
    });

    for (const m of s.members) {
      const a = nodeMap.get(m.i);
      const b = nodeMap.get(m.j);
      if (a === undefined || b === undefined || a === b) continue;
      this.addMemberGraphWithSection(a, b, sectionKeyForMember(m));
    }

    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  /** The authored 2D graph — the SOURCE of a hand-drawn tower (see FreeGraph). */
  exportGraph(): FreeGraph {
    return {
      nodes: this.nodes.map((n) => ({ id: n.id, x: n.x, y: n.y })),
      members: this.members.map((m) => ({ id: m.id, a: m.a, b: m.b, section: m.section, material: m.material })),
      idc: this.idc,
    };
  }

  /** Load an authored 2D graph verbatim — no lossy 3D→2D reconstruction. */
  loadGraph(g: FreeGraph): void {
    this.undoStack = [];
    this.nodes = g.nodes.map((n) => ({ id: n.id, x: n.x, y: n.y }));
    this.members = g.members.map((m) => ({ id: m.id, a: m.a, b: m.b, section: m.section, material: m.material }));
    this.selNodes.clear();
    this.selMembers.clear();
    this.clip = null;
    this.placement = null;
    this.idc = g.idc ?? this.nodes.reduce((mx, n) => Math.max(mx, n.id), 0) + 1;
    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  addMemberAt(x1: number, y1: number, x2: number, y2: number): void {
    this.pushHistory();
    const centerlineStroke =
      this.mirrorMode && Math.abs(x1) <= DRAW_CENTERLINE_INTENT_X && Math.abs(x2) <= DRAW_CENTERLINE_INTENT_X;
    const centerlineStartAnchor =
      this.mirrorMode && Math.abs(x1) <= DRAW_CENTERLINE_ANCHOR_X && Math.abs(x2) > DRAW_CENTERLINE_ANCHOR_X;
    const centerlineEndAnchor =
      this.mirrorMode && Math.abs(x2) <= DRAW_CENTERLINE_ANCHOR_X && Math.abs(x1) > DRAW_CENTERLINE_ANCHOR_X;
    let startPick = this.pickNode(x1, y1);
    let endPick = this.pickNode(x2, y2);
    if (centerlineStroke || centerlineStartAnchor || centerlineEndAnchor) {
      const startNode = startPick !== null ? this.getNode(startPick) : null;
      const endNode = endPick !== null ? this.getNode(endPick) : null;
      if ((centerlineStroke || centerlineStartAnchor) && startNode && Math.abs(startNode.x) > 1e-6) startPick = null;
      if ((centerlineStroke || centerlineEndAnchor) && endNode && Math.abs(endNode.x) > 1e-6) endPick = null;
    }
    const startStep = startPick === null ? DRAW_FALLBACK_GRID : GRID;
    const endStep = endPick === null ? DRAW_FALLBACK_GRID : GRID;
    const start = {
      raw: { x: x1, y: y1 },
      snapped: { x: this.snapTo(x1, startStep), y: Math.max(0, this.snapTo(y1, startStep)) },
    };
    const end = {
      raw: { x: x2, y: y2 },
      snapped: { x: this.snapTo(x2, endStep), y: Math.max(0, this.snapTo(y2, endStep)) },
    };
    if (centerlineStroke || centerlineStartAnchor) {
      start.snapped.x = 0;
    }
    if (centerlineStroke || centerlineEndAnchor) {
      end.snapped.x = 0;
    }
    if (startPick === null && endPick === null) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      if (!centerlineStroke && !centerlineStartAnchor && this.mirrorMode && Math.abs(dx) >= DRAW_AXIS_PRESERVE_MIN && Math.abs(start.snapped.x) < 1e-6) {
        const dir = Math.sign(x1) || Math.sign(dx) || 1;
        start.snapped.x = this.snapTo(dir * DRAW_FALLBACK_GRID, DRAW_FALLBACK_GRID);
      }
      if (Math.abs(dx) >= DRAW_AXIS_PRESERVE_MIN && Math.abs(end.snapped.x - start.snapped.x) < 1e-6) {
        end.snapped.x = this.snapTo(start.snapped.x + Math.sign(dx) * DRAW_FALLBACK_GRID, DRAW_FALLBACK_GRID);
      }
      if (Math.abs(dy) >= DRAW_AXIS_PRESERVE_MIN && Math.abs(end.snapped.y - start.snapped.y) < 1e-6) {
        end.snapped.y = Math.max(
          0,
          this.snapTo(start.snapped.y + Math.sign(dy) * DRAW_FALLBACK_GRID, DRAW_FALLBACK_GRID),
        );
      }
    }
    const a = startPick ?? this.nodeAt(start.snapped.x, start.snapped.y);
    let b = endPick ?? this.nodeAt(end.snapped.x, end.snapped.y);
    if (a === b) {
      const drag = Math.hypot(x2 - x1, y2 - y1);
      if (drag >= DRAW_FORCE_NEW_MIN) {
        const forcedX = this.snapTo(x2, DRAW_FALLBACK_GRID);
        const forcedY = Math.max(0, this.snapTo(y2, DRAW_FALLBACK_GRID));
        const startNode = this.getNode(a);
        const differsFromStart =
          !startNode || Math.abs(startNode.x - forcedX) > 1e-6 || Math.abs(startNode.y - forcedY) > 1e-6;
        if (differsFromStart) {
          b = this.nodeAt(forcedX, forcedY);
        }
      }
    }
    if (a === b) {
      const previous = this.undoStack.pop();
      if (previous) this.restoreState(previous);
      console.info(DEBUG_PREFIX, "draw ignored: start and end resolved to the same node", {
        active: this.active.key,
        start,
        end,
        nodeId: a,
      });
      return;
    }
    const memberId = this.addMemberGraph(a, b);
    console.info(DEBUG_PREFIX, "draw member", {
      active: this.active.key,
      start,
      end,
      startNode: a,
      endNode: b,
      memberId,
      groundAnchors: [a, b].filter((id) => this.isGround(this.getNode(id)?.y ?? Infinity)),
      nodeCount: this.nodes.length,
      memberCount: this.members.length,
    });
    this.clearSelection();
    this.setSelection(null, memberId, false);
    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  undo(): void {
    const state = this.undoStack.pop();
    if (!state) return;
    this.restoreState(state);
    this.redraw();
    this.emitChange();
  }

  hasNodeNear(x: number, y: number): boolean {
    return this.pickNode(x, y) !== null;
  }

  isDrawingMode(): boolean {
    return this.interactionMode === "draw";
  }

  hasSelectedNodeNear(x: number, y: number): boolean {
    const id = this.pickNode(x, y);
    return id !== null && this.selNodes.has(id);
  }

  selectBox(x0: number, y0: number, x1: number, y1: number, add = false): void {
    this.applyBoxSelect(this.snap(x0), Math.max(0, this.snap(y0)), this.snap(x1), Math.max(0, this.snap(y1)), add);
    this.redraw();
  }

  selectAll(): void {
    this.selNodes = new Set(this.nodes.map((n) => n.id));
    this.selMembers = new Set(this.members.map((m) => m.id));
    this.redraw();
  }

  selectAt(x: number, y: number, add = false): void {
    const node = this.pickNode(x, y);
    if (node !== null) {
      this.setSelection(node, null, add);
      this.redraw();
      return;
    }
    const member = this.pickMember(x, y);
    this.setSelection(null, member, add);
    this.redraw();
  }

  /** Is there a joint OR a member under (x,y) that an edit-mode drag can grab? */
  hasGrabbableAt(x: number, y: number): boolean {
    return this.pickNode(x, y) !== null || this.pickMember(x, y) !== null;
  }

  /**
   * Grab the joint/segment under (x,y) to start a move. If it isn't already part
   * of the selection, select just it — so a single click-drag on any joint or
   * line moves it (you no longer have to select first, then drag).
   */
  grabForMove(x: number, y: number): void {
    const nid = this.pickNode(x, y);
    if (nid !== null) {
      if (!this.selNodes.has(nid)) {
        this.clearSelection();
        this.selNodes.add(nid);
        this.redraw();
      }
      return;
    }
    const mid = this.pickMember(x, y);
    if (mid !== null && !this.selMembers.has(mid)) {
      this.clearSelection();
      this.selMembers.add(mid);
      this.redraw();
    }
  }

  moveSelectionBy(dx: number, dy: number): void {
    // Move selected joints AND the endpoints of selected segments, so dragging a
    // selected line drags the whole line (not just its shared joints).
    const ids = new Set<number>(this.selNodes);
    for (const m of this.members) {
      if (this.selMembers.has(m.id)) {
        ids.add(m.a);
        ids.add(m.b);
      }
    }
    if (ids.size === 0) return;
    const sdx = this.snap(dx);
    const sdy = this.snap(dy);
    if (Math.abs(sdx) < 1e-9 && Math.abs(sdy) < 1e-9) return;
    this.pushHistory();
    // Mirror partners move by the MIRRORED delta (−dx, dy) so the two sides stay
    // symmetric while mirror is on. Compute them before moving anything.
    const mirrorMoves = new Map<number, { x: number; y: number }>();
    if (this.mirrorMode) {
      for (const id of ids) {
        const mn = this.mirrorNodeExisting(id);
        if (mn !== null && mn !== id && !ids.has(mn)) mirrorMoves.set(mn, { x: -sdx, y: sdy });
      }
    }
    for (const id of ids) {
      const n = this.getNode(id);
      if (!n) continue;
      n.x = this.snap(n.x + sdx);
      n.y = Math.max(0, this.snap(n.y + sdy));
    }
    for (const [id, d] of mirrorMoves) {
      const n = this.getNode(id);
      if (!n) continue;
      n.x = this.snap(n.x + d.x);
      n.y = Math.max(0, this.snap(n.y + d.y));
    }
    this.redraw();
    this.emitChange();
  }

  getOverlay(): FreeBuildOverlay {
    return {
      nodes: this.nodes.map((n) => ({
        id: n.id,
        x: n.x,
        y: n.y,
        selected: this.selNodes.has(n.id),
        fixed: this.isGround(n.y),
      })),
      members: this.members.map((m) => ({
        id: m.id,
        a: m.a,
        b: m.b,
        section: m.section,
        material: m.material ?? "a572",
        selected: this.selMembers.has(m.id),
      })),
      mirror: { enabled: this.mirrorMode, axisX: 0 },
      placement: this.placementGhost(),
    };
  }

  dispose(): void {
    this.disable();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.clearMemberLines();
    this.opts.scene.remove(this.group);
    this.nodeMesh.geometry.dispose();
    (this.nodeMesh.material as THREE.Material).dispose();
    this.preview.geometry.dispose();
    (this.preview.material as THREE.Material).dispose();
    this.previewNode.geometry.dispose();
    (this.previewNode.material as THREE.Material).dispose();
    this.boxHelper.geometry.dispose();
    (this.boxHelper.material as THREE.Material).dispose();
  }

  // ── graph helpers ────────────────────────────────────────────────────────────

  private nextId(): number {
    return ++this.idc;
  }

  private snapshotState(): GraphState {
    return {
      nodes: this.nodes.map((n) => ({ ...n })),
      members: this.members.map((m) => ({ ...m })),
      selNodes: [...this.selNodes],
      selMembers: [...this.selMembers],
      idc: this.idc,
    };
  }

  private restoreState(state: GraphState): void {
    this.nodes = state.nodes.map((n) => ({ ...n }));
    this.members = state.members.map((m) => ({ ...m }));
    this.selNodes = new Set(state.selNodes);
    this.selMembers = new Set(state.selMembers);
    this.idc = state.idc;
  }

  private pushHistory(): void {
    this.undoStack.push(this.snapshotState());
    if (this.undoStack.length > 100) this.undoStack.shift();
  }

  private getNode(id: number): GNode | undefined {
    return this.nodes.find((n) => n.id === id);
  }

  private isGround(y: number): boolean {
    return Math.abs(y) <= GROUND_EPS;
  }

  private snap(v: number): number {
    return Math.round(v / GRID) * GRID;
  }

  private snapTo(v: number, step: number): number {
    return Math.round(v / step) * step;
  }

  /** Find an existing node near (x,y), else create one. Returns its id. */
  private nodeAt(x: number, y: number): number {
    for (const n of this.nodes) {
      if (Math.abs(n.x - x) < 1e-6 && Math.abs(n.y - y) < 1e-6) return n.id;
    }
    const id = this.nextId();
    this.nodes.push({ id, x, y: Math.max(0, y) });
    return id;
  }

  private mirrorNode(id: number): number | null {
    const n = this.getNode(id);
    if (!n) return null;
    return this.nodeAt(this.snapTo(-n.x, DRAW_FALLBACK_GRID), n.y);
  }

  /** The EXISTING node mirrored across x=0 (or the node itself if on the axis). */
  private mirrorNodeExisting(id: number): number | null {
    const n = this.getNode(id);
    if (!n) return null;
    if (Math.abs(n.x) < 1e-4) return id;
    for (const m of this.nodes) {
      if (Math.abs(m.x + n.x) < 0.05 && Math.abs(m.y - n.y) < 0.05) return m.id;
    }
    return null;
  }

  /** The EXISTING member that mirrors `memberId` across x=0, or null. */
  private mirrorMemberExisting(memberId: number): number | null {
    const m = this.members.find((x) => x.id === memberId);
    if (!m) return null;
    const a = this.mirrorNodeExisting(m.a);
    const b = this.mirrorNodeExisting(m.b);
    if (a === null || b === null) return null;
    const mm = this.members.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));
    return mm && mm.id !== memberId ? mm.id : null;
  }

  /** Selected members plus their mirror partners (when mirror is on) — so an edit
   *  to one side also applies to the other. */
  private selMembersWithMirror(): Set<number> {
    const ids = new Set(this.selMembers);
    if (this.mirrorMode) {
      for (const id of this.selMembers) {
        const mm = this.mirrorMemberExisting(id);
        if (mm !== null) ids.add(mm);
      }
    }
    return ids;
  }

  private selNodesWithMirror(): Set<number> {
    const ids = new Set(this.selNodes);
    if (this.mirrorMode) {
      for (const id of this.selNodes) {
        const mn = this.mirrorNodeExisting(id);
        if (mn !== null) ids.add(mn);
      }
    }
    return ids;
  }

  /** Pick the nearest authored node within PICK_R of world (x,y), or null. */
  private pickNode(x: number, y: number): number | null {
    let best: number | null = null;
    let bestD = PICK_R * PICK_R;
    for (const n of this.nodes) {
      const dx = n.x - x;
      const dy = n.y - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = n.id;
      }
    }
    return best;
  }

  /** Pick the nearest member (by point-segment distance) within a tolerance. */
  private pickMember(x: number, y: number): number | null {
    let best: number | null = null;
    let bestD = MEMBER_PICK_R * MEMBER_PICK_R;
    for (const m of this.members) {
      const a = this.getNode(m.a);
      const b = this.getNode(m.b);
      if (!a || !b) continue;
      const d = segPointDistSq(x, y, a.x, a.y, b.x, b.y);
      if (d < bestD) {
        bestD = d;
        best = m.id;
      }
    }
    return best;
  }

  private splitMemberAt(memberId: number, x: number, y: number): number | null {
    const m = this.members.find((q) => q.id === memberId);
    if (!m) return null;
    const a = this.getNode(m.a);
    const b = this.getNode(m.b);
    if (!a || !b) return null;

    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - a.x) * vx + (y - a.y) * vy) / len2)) : 0;
    if (t < 0.08) return m.a;
    if (t > 0.92) return m.b;

    const nx = this.snap(a.x + vx * t);
    const ny = Math.max(0, this.snap(a.y + vy * t));
    const nodeId = this.nodeAt(nx, ny);
    if (nodeId === m.a || nodeId === m.b) return nodeId;

    this.members = this.members.filter((q) => q.id !== memberId);
    this.selMembers.delete(memberId);
    this.addMemberGraphWithSection(m.a, nodeId, m.section);
    this.addMemberGraphWithSection(nodeId, m.b, m.section);
    return nodeId;
  }

  private addMemberGraph(a: number, b: number): number | null {
    const primary = this.addMemberGraphWithIntersections(a, b, this.activeSection);
    if (!this.mirrorMode) return primary;
    const ma = this.mirrorNode(a);
    const mb = this.mirrorNode(b);
    if (ma !== null && mb !== null) this.addMemberGraphWithIntersections(ma, mb, this.activeSection);
    return primary;
  }

  private addMemberGraphWithIntersections(a: number, b: number, section: SectionKey): number | null {
    const na = this.getNode(a);
    const nb = this.getNode(b);
    if (!na || !nb || a === b) return null;
    const points: Array<{ t: number; id: number }> = [
      { t: 0, id: a },
      { t: 1, id: b },
    ];

    for (const m of [...this.members]) {
      const ea = this.getNode(m.a);
      const eb = this.getNode(m.b);
      if (!ea || !eb) continue;
      const hit = segmentIntersection(na.x, na.y, nb.x, nb.y, ea.x, ea.y, eb.x, eb.y);
      if (!hit) continue;
      const id = this.splitMemberAt(m.id, hit.x, hit.y);
      if (id !== null) points.push({ t: hit.t, id });
    }

    points.sort((p, q) => p.t - q.t);
    let lastId: number | null = null;
    let lastNew: number | null = null;
    for (const p of points) {
      if (p.id === lastId) continue;
      if (lastId !== null) {
        const id = this.addMemberGraphWithSection(lastId, p.id, section);
        if (id !== null) lastNew = id;
      }
      lastId = p.id;
    }
    return lastNew;
  }

  private addMemberGraphWithSection(a: number, b: number, section: SectionKey): number | null {
    if (a === b) return null;
    // avoid duplicate members between the same pair
    for (const m of this.members) {
      if ((m.a === a && m.b === b) || (m.a === b && m.b === a)) return null;
    }
    const id = this.nextId();
    this.members.push({ id, a, b, section, material: this.activeMaterial });
    return id;
  }

  private setSelectedMembersSection(section: SectionKey): void {
    if (this.selMembers.size === 0) return;
    const targets = this.selMembersWithMirror(); // also the mirror side, if mirror is on
    const changed = this.members.some((m) => targets.has(m.id) && m.section !== section);
    if (!changed) return;
    this.pushHistory();
    for (const m of this.members) {
      if (!targets.has(m.id)) continue;
      m.section = section;
    }
    this.refreshPaletteButtons();
    this.redraw();
    this.emitChange();
  }

  /**
   * Pick the material for new members. In edit mode with a selection, it retypes
   * the selected members' material instead (so you can give one leg segment a
   * different grade than the rest).
   */
  setActiveMaterial(material: MaterialKey): void {
    this.activeMaterial = material;
    if (this.interactionMode === "edit" && this.selMembers.size > 0) {
      this.setSelectedMembersMaterial(material);
    }
    this.refreshMaterialSelect();
  }

  /** Pick the exact section (size) for new members — e.g. legXL. In edit mode with
   *  a selection, retypes the selected members instead. */
  setActiveSection(section: SectionKey): void {
    this.activeSection = section;
    this.active = paletteFor(section); // reflect the family on the palette buttons
    if (this.interactionMode === "edit" && this.selMembers.size > 0) {
      this.setSelectedMembersSection(section);
    }
    this.refreshPaletteButtons();
    this.refreshSectionSelect();
  }

  private refreshSectionSelect(): void {
    if (!this.sectionSelect) return;
    let value: SectionKey | null = this.activeSection;
    if (this.interactionMode === "edit" && this.selMembers.size > 0) {
      value = this.selectedMemberSection(); // the selected members' section if uniform
    }
    if (value) this.sectionSelect.value = value;
  }

  private setSelectedMembersMaterial(material: MaterialKey): void {
    if (this.selMembers.size === 0) return;
    const targets = this.selMembersWithMirror(); // also the mirror side, if mirror is on
    const changed = this.members.some((m) => targets.has(m.id) && (m.material ?? "a572") !== material);
    if (!changed) return;
    this.pushHistory();
    for (const m of this.members) {
      if (targets.has(m.id)) m.material = material;
    }
    this.redraw();
    this.emitChange();
  }

  /** Show the selected members' material (if uniform) in the dropdown, else the active one. */
  private refreshMaterialSelect(): void {
    if (!this.materialSelect) return;
    let value: MaterialKey | null = this.activeMaterial;
    if (this.interactionMode === "edit" && this.selMembers.size > 0) {
      const sel = [...this.members].filter((m) => this.selMembers.has(m.id));
      const mats = new Set(sel.map((m) => m.material ?? "a572"));
      value = mats.size === 1 ? ([...mats][0] as MaterialKey) : null;
    }
    if (value) this.materialSelect.value = value;
  }

  /** Remove nodes with no members and not selected — keeps the graph tidy. */
  private pruneOrphans(): void {
    const used = new Set<number>();
    for (const m of this.members) {
      used.add(m.a);
      used.add(m.b);
    }
    this.nodes = this.nodes.filter((n) => used.has(n.id) || this.selNodes.has(n.id));
  }

  private deleteSelection(): void {
    if (this.selNodes.size === 0 && this.selMembers.size === 0) return;
    this.pushHistory();
    // When mirror is on, delete the mirror partners too, so both sides go together.
    const delMembers = this.selMembersWithMirror();
    const delNodes = this.selNodesWithMirror();
    this.members = this.members.filter((m) => !delMembers.has(m.id));
    if (delNodes.size > 0) {
      this.members = this.members.filter((m) => !delNodes.has(m.a) && !delNodes.has(m.b));
      this.nodes = this.nodes.filter((n) => !delNodes.has(n.id));
    }
    this.selNodes.clear();
    this.selMembers.clear();
    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  // ── copy / paste ─────────────────────────────────────────────────────────────

  private copySelection(): void {
    // gather selected nodes + endpoints of selected members
    const ids = new Set<number>(this.selNodes);
    for (const m of this.members) {
      if (this.selMembers.has(m.id)) {
        ids.add(m.a);
        ids.add(m.b);
      }
    }
    if (ids.size === 0) return;

    const nodes: GNode[] = [];
    let minX = Infinity;
    let minY = Infinity;
    for (const id of ids) {
      const n = this.getNode(id);
      if (!n) continue;
      nodes.push({ id: n.id, x: n.x, y: n.y });
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
    }
    // normalize to local origin so paste offset is consistent
    for (const n of nodes) {
      n.x -= minX;
      n.y -= minY;
    }
    // include members whose BOTH endpoints are in the copied set
    const members: GMember[] = [];
    for (const m of this.members) {
      if (ids.has(m.a) && ids.has(m.b)) {
        members.push({ id: m.id, a: m.a, b: m.b, section: m.section });
      }
    }
    this.clip = { nodes, members };
  }

  /**
   * Start placing the clipboard: the copied part FLOATS, following the cursor,
   * ready to drop wherever. While placing: move the mouse to position, scroll to
   * scale, middle-click to rotate 90°, left-click to drop, Esc to cancel. (Was:
   * dumped at a fixed offset on the ground with no way to move it.)
   */
  private paste(): void {
    if (!this.clip || this.clip.nodes.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of this.clip.nodes) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x);
      maxY = Math.max(maxY, n.y);
    }
    this.placement = {
      pos: { x: 0, y: Math.max(2, (maxY - minY) / 2 + 1) }, // sensible default until the cursor moves
      localCenter: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
      scale: 1,
      angle: 0,
    };
    this.emitChange(); // push a fresh overlay so the ghost appears
  }

  /** Map a clip-local point through the current placement transform → world. */
  private placementWorld(lx: number, ly: number): { x: number; y: number } {
    const p = this.placement!;
    const rx = (lx - p.localCenter.x) * p.scale;
    const ry = (ly - p.localCenter.y) * p.scale;
    const c = Math.cos(p.angle);
    const s = Math.sin(p.angle);
    return { x: p.pos.x + rx * c - ry * s, y: p.pos.y + rx * s + ry * c };
  }

  isPlacing(): boolean {
    return this.placement !== null;
  }

  /** Move the floating part so its centre sits at (x, y), with snapping. */
  placementMoveTo(x: number, y: number): void {
    if (!this.placement) return;
    let px = this.snap(x);
    let py = Math.max(0, this.snap(y));
    // Snap the part's centre onto the tower centreline (x = 0) when close, so a
    // scaled segment nests centred on the axis of a tapering tower.
    if (Math.abs(px) <= PLACE_CENTERLINE_SNAP) px = 0;
    this.placement.pos = { x: px, y: py };
    // Then try to mate: if any of the part's nodes lands near an existing joint,
    // nudge the whole part so they coincide (snap onto the top of a segment).
    const mate = this.placementMateOffset();
    if (mate) {
      this.placement.pos = { x: px + mate.dx, y: Math.max(0, py + mate.dy) };
    }
    this.emitChange();
  }

  /** Smallest offset that snaps a placement node onto a nearby existing joint. */
  private placementMateOffset(): { dx: number; dy: number } | null {
    if (!this.placement || !this.clip) return null;
    let best: { dx: number; dy: number; d: number } | null = null;
    for (const cn of this.clip.nodes) {
      const w = this.placementWorld(cn.x, cn.y);
      for (const n of this.nodes) {
        const dx = n.x - w.x;
        const dy = n.y - w.y;
        const d = Math.hypot(dx, dy);
        if (d <= PLACE_MATE_SNAP && (!best || d < best.d)) best = { dx, dy, d };
      }
    }
    return best ? { dx: best.dx, dy: best.dy } : null;
  }

  /** Scale the floating part about its centre (wheel while placing). */
  placementScaleBy(factor: number): void {
    if (!this.placement) return;
    this.placement.scale = Math.max(0.2, Math.min(6, this.placement.scale * factor));
    this.emitChange();
  }

  /** Rotate the floating part 90° (middle-click while placing). */
  placementRotate(): void {
    if (!this.placement) return;
    this.placement.angle = (this.placement.angle + Math.PI / 2) % (Math.PI * 2);
    this.emitChange();
  }

  /** Drop the floating part into the graph at its current transform. */
  placementCommit(): void {
    if (!this.placement || !this.clip) return;
    this.pushHistory();
    this.selNodes.clear();
    this.selMembers.clear();
    const remap = new Map<number, number>();
    for (const n of this.clip.nodes) {
      const w = this.placementWorld(n.x, n.y);
      const id = this.nextId();
      remap.set(n.id, id);
      this.nodes.push({ id, x: this.snap(w.x), y: Math.max(0, this.snap(w.y)) });
      this.selNodes.add(id);
    }
    for (const m of this.clip.members) {
      const a = remap.get(m.a);
      const b = remap.get(m.b);
      if (a === undefined || b === undefined) continue;
      const id = this.nextId();
      this.members.push({ id, a, b, section: m.section });
      this.selMembers.add(id);
    }
    this.placement = null;
    this.redraw();
    this.emitChange();
  }

  placementCancel(): void {
    if (!this.placement) return;
    this.placement = null;
    this.redraw();
    this.emitChange();
  }

  /** The floating ghost in world coords, for the 2D view to render (or null). */
  private placementGhost(): PlacementGhost | null {
    if (!this.placement || !this.clip) return null;
    const nodes = this.clip.nodes.map((n) => this.placementWorld(n.x, n.y));
    const byId = new Map(this.clip.nodes.map((n) => [n.id, n]));
    const members = this.clip.members
      .map((m) => {
        const a = byId.get(m.a);
        const b = byId.get(m.b);
        if (!a || !b) return null;
        const wa = this.placementWorld(a.x, a.y);
        const wb = this.placementWorld(b.x, b.y);
        return { x1: wa.x, y1: wa.y, x2: wb.x, y2: wb.y, section: m.section };
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
    return { members, nodes, scale: this.placement.scale, angleDeg: Math.round((this.placement.angle * 180) / Math.PI) };
  }

  private repeatSelection(): void {
    const ids = this.selectedGraphNodeIds();
    if (ids.size === 0) return;

    const srcNodes = [...ids]
      .map((id) => this.getNode(id))
      .filter((n): n is GNode => !!n);
    if (srcNodes.length === 0) return;
    this.pushHistory();

    const srcMembers = this.members.filter((m) => ids.has(m.a) && ids.has(m.b));
    const minX = Math.min(...srcNodes.map((n) => n.x));
    const maxX = Math.max(...srcNodes.map((n) => n.x));
    const minY = Math.min(...srcNodes.map((n) => n.y));
    const maxY = Math.max(...srcNodes.map((n) => n.y));
    const width = Math.max(1e-6, maxX - minX);
    const height = maxY - minY;
    const rise = this.repeatRise();
    const topScale = this.repeatScale();
    const remap = new Map<number, number>();

    this.selNodes.clear();
    this.selMembers.clear();

    if (height <= 1e-6) {
      const cx = (minX + maxX) * 0.5;
      for (const n of srcNodes) {
        const id = this.nodeAt(
          this.snap(cx + (n.x - cx) * topScale),
          Math.max(0, this.snap(n.y + rise)),
        );
        remap.set(n.id, id);
        this.selNodes.add(id);
      }
    } else {
      const sourceBottom = srcNodes.filter((n) => Math.abs(n.y - minY) < 1e-6);
      const sourceTop = [...srcNodes.filter((n) => Math.abs(n.y - maxY) < 1e-6)].sort((a, b) => a.x - b.x);
      const reuseByBottom = new Map<number, number>();
      if (sourceBottom.length === sourceTop.length && sourceBottom.length > 0) {
        [...sourceBottom]
          .sort((a, b) => a.x - b.x)
          .forEach((bottom, i) => reuseByBottom.set(bottom.id, sourceTop[i]!.id));
      }

      const bottomXs = sourceTop.length > 0 ? sourceTop.map((n) => n.x) : [minX, maxX];
      const bottomMinX = Math.min(...bottomXs);
      const bottomMaxX = Math.max(...bottomXs);
      const bottomCenter = (bottomMinX + bottomMaxX) * 0.5;
      const bottomWidth = Math.max(1e-6, bottomMaxX - bottomMinX);
      const nextTopWidth = bottomWidth * topScale;

      for (const n of srcNodes) {
        const reused = reuseByBottom.get(n.id);
        if (reused !== undefined) {
          remap.set(n.id, reused);
          continue;
        }
        const u = (n.x - minX) / width;
        const t = (n.y - minY) / height;
        const rowWidth = bottomWidth + (nextTopWidth - bottomWidth) * t;
        const id = this.nodeAt(
          this.snap(bottomCenter + (u - 0.5) * rowWidth),
          Math.max(0, this.snap(maxY + t * rise)),
        );
        remap.set(n.id, id);
        this.selNodes.add(id);
      }
    }

    for (const m of srcMembers) {
      const a = remap.get(m.a);
      const b = remap.get(m.b);
      if (a === undefined || b === undefined) continue;
      const id = this.addMemberGraphWithSection(a, b, m.section);
      if (id !== null) this.selMembers.add(id);
    }

    this.pruneOrphans();
    this.redraw();
    this.emitChange();
  }

  private selectedGraphNodeIds(): Set<number> {
    const ids = new Set<number>(this.selNodes);
    for (const m of this.members) {
      if (this.selMembers.has(m.id)) {
        ids.add(m.a);
        ids.add(m.b);
      }
    }
    return ids;
  }

  private selectedHeight(): number {
    const ys = [...this.selectedGraphNodeIds()]
      .map((id) => this.getNode(id)?.y)
      .filter((y): y is number => y !== undefined);
    return ys.length > 0 ? Math.max(...ys) - Math.min(...ys) : 0;
  }

  private repeatRise(): number {
    const v = parseFloat(this.repeatRiseInput?.value ?? "");
    return Number.isFinite(v) && v > 0 ? v : this.selectedHeight() || 5;
  }

  private repeatScale(): number {
    const v = parseFloat(this.repeatScaleInput?.value ?? "");
    return Number.isFinite(v) && v > 0 ? v : 0.9;
  }

  // ── 2D → 3D extrusion (stability) ────────────────────────────────────────────

  private extrude(): Structure {
    const s: Structure = { nodes: [], members: [] };
    const faces = this.opts.getFaces?.() === 3 ? 3 : 4;

    // The drawn 2D graph is ONE face of the tower. We wrap it identically onto all
    // `faces` sides of a regular polygon so every side is the SAME (a real N-legged
    // tower), sharing the corner legs between adjacent faces — instead of the old
    // front/back-only box, which drew a different shape side-on and could not model
    // an odd (3-legged) tower.

    // Per-row horizontal bounds of the authored face (min/max x at each snapped y).
    const rows = new Map<number, { min: number; max: number }>();
    for (const n of this.nodes) {
      const y = this.snap(n.y);
      const r = rows.get(y);
      if (r) {
        r.min = Math.min(r.min, n.x);
        r.max = Math.max(r.max, n.x);
      } else {
        rows.set(y, { min: n.x, max: n.x });
      }
    }

    // Regular polygon geometry. Face k spans the edge from vertex k → vertex k+1; a
    // node's local coordinate u = (x − rowMin)/rowWidth ∈ [0,1] maps across that
    // edge. The circumradius follows the authored face width: the edge chord equals
    // the width, and chord = 2·R·sin(π/faces), so R = width / (2·sin(π/faces)).
    const half = Math.PI / faces;
    const chord = 2 * Math.sin(half);
    const CORNER_EPS = 1e-4;
    const vertex = (k: number, R: number): [number, number] => {
      const a = (k / faces) * Math.PI * 2 + half;
      return [Math.cos(a) * R, Math.sin(a) * R];
    };
    const radiusAt = (y: number): number => {
      const r = rows.get(this.snap(y));
      const w = r ? r.max - r.min : 0;
      return (w > CORNER_EPS ? w : FALLBACK_DEPTH) / chord;
    };

    // Node de-dup: corner (leg) nodes are shared between adjacent faces (keyed by
    // polygon vertex + row); a zero-width row collapses to a single shared centre
    // node (so an A-frame becomes a proper pyramid apex); interior nodes are
    // per-face. The u-interpolation lands corners exactly on their polygon vertex,
    // so a leg shared as face k's left edge and face (k−1)'s right edge coincides.
    const nodeIndex = new Map<string, number>();
    const place = (gn: GNode, k: number): number => {
      const yKey = this.snap(gn.y);
      const r = rows.get(yKey);
      const w = r ? r.max - r.min : 0;
      let key: string;
      let px = 0;
      let pz = 0;
      if (!r || w <= CORNER_EPS) {
        key = `C:${yKey}`; // collapsed axis node (single-column row / apex)
      } else {
        const u = (gn.x - r.min) / w;
        const R = radiusAt(gn.y);
        const [x0, z0] = vertex(k, R);
        const [x1, z1] = vertex((k + 1) % faces, R);
        px = x0 + (x1 - x0) * u;
        pz = z0 + (z1 - z0) * u;
        if (Math.abs(gn.x - r.min) <= CORNER_EPS) key = `L:${k}:${yKey}`;
        else if (Math.abs(gn.x - r.max) <= CORNER_EPS) key = `L:${(k + 1) % faces}:${yKey}`;
        else key = `F:${k}:${gn.id}`;
      }
      let idx = nodeIndex.get(key);
      if (idx === undefined) {
        idx = s.nodes.length;
        s.nodes.push(makeNode(px, gn.y, pz, this.isGround(gn.y)));
        nodeIndex.set(key, idx);
      }
      return idx;
    };

    const secOf = (k: SectionKey): Section => SECTIONS[k];
    const optsFor = (m: GMember): MemberOptions => {
      const guy = isGuyKey(m.section);
      // Per-member material if set; otherwise the sensible default for the section.
      const material = m.material ? MATERIALS[m.material] : guy ? GUY_STRAND : undefined;
      const opt: MemberOptions = {};
      if (material) opt.material = material;
      if (guy) {
        opt.tensionOnly = true;
        opt.pretensionStrain = 0.0015;
      }
      return opt;
    };

    // Wrap every authored member onto every face, de-duplicating members that land
    // on a shared leg (a leg segment is authored on the left of one face and the
    // right of its neighbour).
    const memberSeen = new Set<string>();
    for (let k = 0; k < faces; k++) {
      for (const m of this.members) {
        const a = this.getNode(m.a);
        const b = this.getNode(m.b);
        if (!a || !b) continue;
        const ai = place(a, k);
        const bi = place(b, k);
        if (ai === bi) continue;
        const mkey = `${Math.min(ai, bi)}-${Math.max(ai, bi)}-${m.section}-${m.material ?? ""}`;
        if (memberSeen.has(mkey)) continue;
        memberSeen.add(mkey);
        addMember(s, ai, bi, secOf(m.section), optsFor(m));
      }
    }

    computeMasses(s);
    return s;
  }

  // ── rendering the editing preview ────────────────────────────────────────────

  private clearMemberLines(): void {
    for (const line of this.memberLines.values()) {
      this.memberGroup.remove(line);
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    this.memberLines.clear();
  }

  private redraw(): void {
    this.drawNodes();
    this.drawMembers();
    this.updateHint();
  }

  private drawNodes(): void {
    this.nodeIndexToId = [];
    let i = 0;
    for (const n of this.nodes) {
      if (i >= FreeBuilder.MAX_NODES) break;
      this._dummy.position.set(n.x, n.y, 0);
      this._dummy.scale.setScalar(this.selNodes.has(n.id) ? 1.5 : 1);
      this._dummy.updateMatrix();
      this.nodeMesh.setMatrixAt(i, this._dummy.matrix);
      const c = this.selNodes.has(n.id)
        ? C_SEL
        : this.isGround(n.y)
          ? C_ANCHOR
          : C_NODE;
      this.nodeMesh.setColorAt(i, this._col.setHex(c));
      this.nodeIndexToId[i] = n.id;
      i++;
    }
    this.nodeMesh.count = i;
    this.nodeMesh.instanceMatrix.needsUpdate = true;
    if (this.nodeMesh.instanceColor) this.nodeMesh.instanceColor.needsUpdate = true;
  }

  private drawMembers(): void {
    // rebuild the set of lines (simple + robust for an editor at this scale)
    const seen = new Set<number>();
    for (const m of this.members) {
      const a = this.getNode(m.a);
      const b = this.getNode(m.b);
      if (!a || !b) continue;
      seen.add(m.id);
      let line = this.memberLines.get(m.id);
      if (!line) {
        const geo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(),
          new THREE.Vector3(),
        ]);
        line = new THREE.Line(geo, new THREE.LineBasicMaterial());
        this.memberGroup.add(line);
        this.memberLines.set(m.id, line);
      }
      const pos = line.geometry.getAttribute("position") as THREE.BufferAttribute;
      pos.setXYZ(0, a.x, a.y, 0);
      pos.setXYZ(1, b.x, b.y, 0);
      pos.needsUpdate = true;
      line.geometry.computeBoundingSphere();
      const pal = paletteFor(m.section);
      const mat = line.material as THREE.LineBasicMaterial;
      mat.color.setHex(this.selMembers.has(m.id) ? C_SEL : pal.color);
      mat.linewidth = 2;
    }
    // remove lines for deleted members
    for (const [id, line] of this.memberLines) {
      if (!seen.has(id)) {
        this.memberGroup.remove(line);
        line.geometry.dispose();
        (line.material as THREE.Material).dispose();
        this.memberLines.delete(id);
      }
    }
  }

  // ── pointer → world ──────────────────────────────────────────────────────────

  private worldFromEvent(e: PointerEvent): THREE.Vector3 | null {
    const rect = this.opts.domElement.getBoundingClientRect();
    this.ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.opts.getCamera());
    const hit = this.raycaster.ray.intersectPlane(this.plane, this.hitPt);
    return hit ? this.hitPt : null;
  }

  // ── pointer handlers ─────────────────────────────────────────────────────────

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.enabled || (e.button !== 0 && e.button !== 1)) return;
    if (!this.isScenePointer(e)) return;
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      (this.opts.domElement as HTMLElement).setPointerCapture?.(e.pointerId);
      this.mode = "panning";
      this.dragged = false;
      this.panLast = { x: e.clientX, y: e.clientY };
      this.preview.visible = false;
      this.boxHelper.visible = false;
      return;
    }
    const w = this.worldFromEvent(e);
    if (!w) return;
    e.preventDefault();
    e.stopPropagation();
    (this.opts.domElement as HTMLElement).setPointerCapture?.(e.pointerId);

    this.dragged = false;
    this.downPt.copy(w);
    this.downOnNode = this.pickNode(w.x, w.y);
    this.downOnMember = this.downOnNode === null ? this.pickMember(w.x, w.y) : null;

    if (this.downOnNode !== null) {
      // start a drag-from-node: could become a move (if node is selected and we
      // began ON a selected node) or a new member draw. Decide on first move.
      const id = this.downOnNode;
      if (this.selNodes.has(id) && this.selNodes.size > 0) {
        // begin a potential MOVE of the current selection
        this.beginMove(w);
      } else {
        // begin a potential DRAW from this node
        this.mode = "drawing";
        this.drawFromId = id;
        this.preview.visible = true;
      }
    } else {
      // empty space: potential box-select (with drag) or node placement (on click)
      this.mode = "boxing";
      this.boxStart.set(this.snap(w.x), Math.max(0, this.snap(w.y)), 0);
    }
  };

  private isScenePointer(e: PointerEvent): boolean {
    const target = e.target as HTMLElement | null;
    return !!target && target.id === "scene";
  }

  private beginMove(w: THREE.Vector3): void {
    this.pushHistory();
    this.mode = "moving";
    this.moveStart = { x: w.x, y: w.y };
    this.moveOrig.clear();
    for (const id of this.selNodes) {
      const n = this.getNode(id);
      if (n) this.moveOrig.set(id, { x: n.x, y: n.y });
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    if (this.mode === "panning") {
      const dx = e.clientX - this.panLast.x;
      const dy = e.clientY - this.panLast.y;
      if (dx !== 0 || dy !== 0) {
        this.panViewPixels(dx, dy);
        this.panLast = { x: e.clientX, y: e.clientY };
        this.dragged = true;
      }
      e.preventDefault();
      return;
    }
    const w = this.worldFromEvent(e);
    if (!w) return;

    // ghost node under cursor when idle (shows where a click would drop a node)
    if (this.mode === "idle") {
      const near = this.pickNode(w.x, w.y);
      if (near === null) {
        this.previewNode.visible = true;
        this.previewNode.position.set(
          this.snap(w.x),
          Math.max(0, this.snap(w.y)),
          0,
        );
      } else {
        this.previewNode.visible = false;
      }
      return;
    }

    const dx = w.x - this.downPt.x;
    const dy = w.y - this.downPt.y;
    if (!this.dragged && dx * dx + dy * dy > 0.15 * 0.15) this.dragged = true;

    if (this.mode === "boxing" && this.downOnMember !== null && this.dragged) {
      this.pushHistory();
      this.drawFromId = this.splitMemberAt(this.downOnMember, this.downPt.x, this.downPt.y);
      this.downOnNode = this.drawFromId;
      this.downOnMember = null;
      this.mode = "drawing";
      this.preview.visible = this.drawFromId !== null;
      this.boxHelper.visible = false;
      this.redraw();
      if (this.drawFromId !== null) this.emitChange();
    }

    if (this.mode === "drawing" && this.drawFromId !== null) {
      const from = this.getNode(this.drawFromId);
      if (from) {
        const tx = this.snap(w.x);
        const ty = Math.max(0, this.snap(w.y));
        const pos = this.preview.geometry.getAttribute(
          "position",
        ) as THREE.BufferAttribute;
        pos.setXYZ(0, from.x, from.y, 0);
        pos.setXYZ(1, tx, ty, 0);
        pos.needsUpdate = true;
        this.preview.geometry.computeBoundingSphere();
      }
    } else if (this.mode === "moving") {
      const sdx = this.snap(w.x - this.moveStart.x);
      const sdy = this.snap(w.y - this.moveStart.y);
      for (const [id, o] of this.moveOrig) {
        const n = this.getNode(id);
        if (n) {
          n.x = o.x + sdx;
          n.y = Math.max(0, o.y + sdy);
        }
      }
      this.redraw();
    } else if (this.mode === "boxing" && this.dragged) {
      this.updateBox(this.boxStart.x, this.boxStart.y, this.snap(w.x), this.snap(w.y));
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.enabled) return;
    (this.opts.domElement as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (this.mode === "panning") {
      this.mode = "idle";
      return;
    }
    const w = this.worldFromEvent(e) ?? this.downPt;

    if (this.mode === "drawing" && this.drawFromId !== null) {
      if (this.dragged) {
        this.pushHistory();
        // finish a member: land on an existing node or create the end node.
        const target =
          this.pickNode(w.x, w.y) ??
          this.nodeAt(this.snap(w.x), Math.max(0, this.snap(w.y)));
        this.addMemberGraph(this.drawFromId, target);
        this.clearSelection();
        this.redraw();
        this.emitChange();
      } else {
        // click on a node (no drag) → select just it
        this.setSelection(this.drawFromId, null, e.shiftKey);
        this.redraw();
      }
      this.preview.visible = false;
    } else if (this.mode === "moving") {
      if (this.dragged) this.emitChange();
      else this.setSelection(this.downOnNode, null, e.shiftKey);
      this.redraw();
    } else if (this.mode === "boxing") {
      if (this.dragged) {
        this.applyBoxSelect(this.boxStart.x, this.boxStart.y, this.snap(w.x), this.snap(w.y), e.shiftKey);
      } else if (this.downOnMember !== null) {
        this.setSelection(null, this.downOnMember, e.shiftKey);
      } else {
        // click empty space: place a node (also clears selection unless shift)
        this.pushHistory();
        if (!e.shiftKey) this.clearSelection();
        const id = this.nodeAt(this.snap(this.downPt.x), Math.max(0, this.snap(this.downPt.y)));
        if (this.mirrorMode) this.mirrorNode(id);
        this.emitChange();
      }
      this.boxHelper.visible = false;
      this.redraw();
    }

    this.mode = "idle";
    this.drawFromId = null;
    this.downOnNode = null;
    this.downOnMember = null;
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    const target = e.target as HTMLElement | null;
    if (!target || target.id !== "scene") return;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.zoomView(factor);
    e.preventDefault();
  };

  // ── selection ────────────────────────────────────────────────────────────────

  private clearSelection(): void {
    this.selNodes.clear();
    this.selMembers.clear();
    this.refreshPaletteButtons();
  }

  private setSelection(node: number | null, member: number | null, add: boolean): void {
    if (!add) this.clearSelection();
    if (node !== null) {
      if (add && this.selNodes.has(node)) this.selNodes.delete(node);
      else this.selNodes.add(node);
    }
    if (member !== null) {
      if (add && this.selMembers.has(member)) this.selMembers.delete(member);
      else this.selMembers.add(member);
    }
    this.refreshPaletteButtons();
    this.updateHint();
  }

  private applyBoxSelect(x0: number, y0: number, x1: number, y1: number, add: boolean): void {
    const lox = Math.min(x0, x1);
    const hix = Math.max(x0, x1);
    const loy = Math.min(y0, y1);
    const hiy = Math.max(y0, y1);
    if (!add) this.clearSelection();
    for (const n of this.nodes) {
      if (n.x >= lox && n.x <= hix && n.y >= loy && n.y <= hiy) this.selNodes.add(n.id);
    }
    // A member is selected if either endpoint is inside the box or the member
    // crosses the rectangle. This matches visual drag-selection expectations.
    for (const m of this.members) {
      const a = this.getNode(m.a);
      const b = this.getNode(m.b);
      if (!a || !b) continue;
      if (segmentIntersectsRect(a.x, a.y, b.x, b.y, lox, loy, hix, hiy)) {
        this.selMembers.add(m.id);
      }
    }
    this.refreshPaletteButtons();
    this.updateHint();
  }

  private updateBox(x0: number, y0: number, x1: number, y1: number): void {
    const z = 0.01;
    const pts = [
      new THREE.Vector3(x0, y0, z), new THREE.Vector3(x1, y0, z),
      new THREE.Vector3(x1, y0, z), new THREE.Vector3(x1, y1, z),
      new THREE.Vector3(x1, y1, z), new THREE.Vector3(x0, y1, z),
      new THREE.Vector3(x0, y1, z), new THREE.Vector3(x0, y0, z),
    ];
    this.boxHelper.geometry.setFromPoints(pts);
    this.boxHelper.computeLineDistances();
    this.boxHelper.visible = true;
  }

  // ── keyboard ─────────────────────────────────────────────────────────────────

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest("input,select,textarea")) return;
    const meta = e.ctrlKey || e.metaKey;
    if (meta && (e.key === "z" || e.key === "Z")) {
      this.undo();
      e.preventDefault();
    } else if (meta && (e.key === "a" || e.key === "A")) {
      this.selectAll();
      e.preventDefault();
    } else if (meta && (e.key === "c" || e.key === "C")) {
      this.copySelection();
      e.preventDefault();
    } else if (meta && (e.key === "v" || e.key === "V")) {
      this.paste();
      e.preventDefault();
    } else if (e.key === "Delete" || e.key === "Backspace") {
      this.deleteSelection();
      e.preventDefault();
    } else if (e.key === "r" || e.key === "R") {
      this.repeatSelection();
      e.preventDefault();
    } else if (!meta && (e.key === "e" || e.key === "E")) {
      this.setInteractionMode("edit");
      e.preventDefault();
    } else if (!meta && (e.key === "d" || e.key === "D")) {
      this.setInteractionMode("draw");
      e.preventDefault();
    } else if (!meta && (e.key === "m" || e.key === "M")) {
      this.mirrorMode = !this.mirrorMode;
      this.refreshMirrorButton();
      this.updateHint();
      e.preventDefault();
    } else if (!meta && e.key >= "1" && e.key <= String(PALETTE.length)) {
      // 1..N pick the member type (Leg/Brace/Rod/Guy). If members are selected in
      // edit mode, retype them; otherwise arm that type and switch to draw — same
      // as clicking the palette button.
      const item = PALETTE[Number(e.key) - 1];
      if (item) {
        if (this.interactionMode === "edit" && this.selMembers.size > 0) {
          this.setSelectedMembersSection(item.key);
        } else {
          this.active = item;
          this.activeSection = item.key;
          this.setInteractionMode("draw");
        }
        this.refreshPaletteButtons();
      }
      e.preventDefault();
    } else if (
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown"
    ) {
      const step = this.panStep();
      const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
      const dy = e.key === "ArrowDown" ? -step : e.key === "ArrowUp" ? step : 0;
      this.panViewWorld(dx, dy);
      e.preventDefault();
    } else if (e.key === "+" || e.key === "=") {
      this.zoomView(1.15);
      e.preventDefault();
    } else if (e.key === "-" || e.key === "_") {
      this.zoomView(1 / 1.15);
      e.preventDefault();
    } else if (e.key === "Escape") {
      this.clearSelection();
      this.mode = "idle";
      this.preview.visible = false;
      this.boxHelper.visible = false;
      this.redraw();
    }
  };

  private setInteractionMode(mode: "edit" | "draw"): void {
    if (this.interactionMode !== mode) console.info(DEBUG_PREFIX, "mode", mode);
    this.interactionMode = mode;
    this.refreshModeControls();
    this.refreshPaletteButtons();
    this.updateHint();
  }

  private panViewPixels(dx: number, dy: number): void {
    const cam = this.opts.getCamera();
    if (!(cam instanceof THREE.OrthographicCamera)) return;
    const rect = this.opts.domElement.getBoundingClientRect();
    const worldW = (cam.right - cam.left) / cam.zoom;
    const worldH = (cam.top - cam.bottom) / cam.zoom;
    this.panViewWorld((-dx / Math.max(1, rect.width)) * worldW, (dy / Math.max(1, rect.height)) * worldH);
  }

  private panViewWorld(dx: number, dy: number): void {
    const cam = this.opts.getCamera();
    if (!(cam instanceof THREE.OrthographicCamera)) return;
    cam.position.x += dx;
    cam.position.y += dy;
    cam.updateMatrixWorld();
  }

  private panStep(): number {
    const cam = this.opts.getCamera();
    if (!(cam instanceof THREE.OrthographicCamera)) return 2;
    return Math.max(0.5, ((cam.top - cam.bottom) / cam.zoom) * 0.08);
  }

  private zoomView(factor: number): void {
    const cam = this.opts.getCamera();
    if (!(cam instanceof THREE.OrthographicCamera)) return;
    cam.zoom = Math.max(0.25, Math.min(12, cam.zoom * factor));
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  }

  // ── change notification (debounced) ──────────────────────────────────────────

  private emitChange(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.opts.onChange(this.extrude());
    }, DEBOUNCE_MS);
  }

  // ── toolbar UI (dark, inline styles, built inside container) ──────────────────

  private buildToolbar(): void {
    if (this.toolbar) return;
    const bar = document.createElement("div");
    Object.assign(bar.style, {
      position: this.toolbarFloating ? "absolute" : "static",
      top: this.toolbarFloating ? "12px" : "",
      left: this.toolbarFloating ? "12px" : "",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      padding: "8px",
      background: this.toolbarFloating ? "rgba(18,22,28,0.92)" : "#0d1219",
      border: "1px solid #2a323c",
      borderRadius: "8px",
      font: "12px/1.3 system-ui, sans-serif",
      color: "#cdd6e0",
      zIndex: "20",
      userSelect: "none",
      cursor: this.toolbarFloating ? "grab" : "default",
    } as CSSStyleDeclaration);

    const title = document.createElement("div");
    title.textContent = "BUILD";
    Object.assign(title.style, {
      fontWeight: "700",
      letterSpacing: "0.08em",
      color: "#9fb2c2",
      cursor: this.toolbarFloating ? "grab" : "default",
    } as CSSStyleDeclaration);
    bar.appendChild(title);
    if (this.toolbarFloating) this.makeToolbarDraggable(bar);

    const facesWrap = document.createElement("label");
    Object.assign(facesWrap.style, {
      display: "grid",
      gap: "3px",
      color: "#7f8b99",
      fontSize: "11px",
    } as CSSStyleDeclaration);
    facesWrap.appendChild(document.createTextNode("Faces"));
    const facesSel = document.createElement("select");
    Object.assign(facesSel.style, {
      width: "100%",
      boxSizing: "border-box",
      padding: "5px 6px",
      background: "#111823",
      border: "1px solid #2a323c",
      borderRadius: "6px",
      color: "#cdd6e0",
      font: "12px system-ui, sans-serif",
    } as CSSStyleDeclaration);
    for (const [value, text] of [["3", "3 — triangular"], ["4", "4 — square"]] as const) {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = text;
      if (Number(value) === (this.opts.getFaces?.() ?? 4)) opt.selected = true;
      facesSel.appendChild(opt);
    }
    facesSel.addEventListener("change", () => {
      const faces = Number(facesSel.value) === 3 ? 3 : 4;
      this.opts.setFaces?.(faces);
      this.emitChange();
    });
    facesWrap.appendChild(facesSel);
    bar.appendChild(facesWrap);

    this.modeButtons = [];
    const modeWrap = document.createElement("div");
    Object.assign(modeWrap.style, {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "6px",
    } as CSSStyleDeclaration);
    const mkMode = (mode: "edit" | "draw", label: string) => {
      const b = document.createElement("button");
      b.textContent = label;
      b.title = mode === "edit" ? "Edit mode (E)" : "Draw mode (D)";
      b.dataset.mode = mode;
      Object.assign(b.style, {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 8px",
        background: "#1b2129",
        border: "1px solid #2a323c",
        borderRadius: "6px",
        color: "#cdd6e0",
        cursor: "pointer",
      } as CSSStyleDeclaration);
      b.appendChild(this.keycap(mode === "edit" ? "E" : "D"));
      b.addEventListener("click", () => this.setInteractionMode(mode));
      this.modeButtons.push(b);
      modeWrap.appendChild(b);
    };
    mkMode("edit", "Edit");
    mkMode("draw", "Draw");
    bar.appendChild(modeWrap);

    this.modeIndicator = document.createElement("div");
    Object.assign(this.modeIndicator.style, {
      padding: "5px 7px",
      borderRadius: "6px",
      border: "1px solid #2a323c",
      fontWeight: "700",
      letterSpacing: "0.04em",
      textAlign: "center",
    } as CSSStyleDeclaration);
    bar.appendChild(this.modeIndicator);

    this.paletteButtons = [];
    PALETTE.forEach((p, i) => {
      const btn = document.createElement("button");
      btn.textContent = p.label;
      Object.assign(btn.style, {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 10px",
        background: "#1b2129",
        border: "1px solid #2a323c",
        borderRadius: "6px",
        color: "#cdd6e0",
        cursor: "pointer",
        textAlign: "left",
      } as CSSStyleDeclaration);
      const swatch = document.createElement("span");
      Object.assign(swatch.style, {
        width: "12px",
        height: "12px",
        borderRadius: "3px",
        flex: "0 0 auto",
        background: "#" + p.color.toString(16).padStart(6, "0"),
      } as CSSStyleDeclaration);
      btn.prepend(swatch);
      btn.appendChild(this.keycap(String(i + 1)));
      btn.addEventListener("click", () => {
        const item = PALETTE[i]!;
        if (this.interactionMode === "edit" && this.selMembers.size > 0) {
          this.setSelectedMembersSection(item.key);
        } else {
          this.active = item;
          this.activeSection = item.key;
          this.setInteractionMode("draw");
        }
        this.refreshPaletteButtons();
      });
      this.paletteButtons.push(btn);
      bar.appendChild(btn);
    });

    // section-size picker: the exact profile for new members (Pipe 2"…8", angles,
    // rods, flats, guys) — or retypes the selected members in edit mode.
    const secWrap = document.createElement("label");
    Object.assign(secWrap.style, {
      display: "grid",
      gap: "3px",
      color: "#7f8b99",
      fontSize: "11px",
      marginTop: "2px",
    } as CSSStyleDeclaration);
    secWrap.appendChild(document.createTextNode("Section size"));
    const secSel = document.createElement("select");
    Object.assign(secSel.style, {
      width: "100%",
      boxSizing: "border-box",
      padding: "5px 6px",
      background: "#111823",
      border: "1px solid #2a323c",
      borderRadius: "6px",
      color: "#cdd6e0",
      font: "12px system-ui, sans-serif",
    } as CSSStyleDeclaration);
    for (const key of Object.keys(SECTIONS) as SectionKey[]) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = `${key} — ${SECTIONS[key].name}`;
      if (key === this.activeSection) opt.selected = true;
      secSel.appendChild(opt);
    }
    secSel.addEventListener("change", () => this.setActiveSection(secSel.value as SectionKey));
    secWrap.appendChild(secSel);
    this.sectionSelect = secSel;
    bar.appendChild(secWrap);

    // material picker: the metal/grade for new members (or the selected ones)
    const matWrap = document.createElement("label");
    Object.assign(matWrap.style, {
      display: "grid",
      gap: "3px",
      color: "#7f8b99",
      fontSize: "11px",
      marginTop: "2px",
    } as CSSStyleDeclaration);
    matWrap.appendChild(document.createTextNode("Material"));
    const matSel = document.createElement("select");
    Object.assign(matSel.style, {
      width: "100%",
      boxSizing: "border-box",
      padding: "5px 6px",
      background: "#111823",
      border: "1px solid #2a323c",
      borderRadius: "6px",
      color: "#cdd6e0",
      font: "12px system-ui, sans-serif",
    } as CSSStyleDeclaration);
    for (const key of Object.keys(MATERIALS) as MaterialKey[]) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = MATERIALS[key].name;
      if (key === this.activeMaterial) opt.selected = true;
      matSel.appendChild(opt);
    }
    matSel.addEventListener("change", () => this.setActiveMaterial(matSel.value as MaterialKey));
    matWrap.appendChild(matSel);
    this.materialSelect = matSel;
    bar.appendChild(matWrap);

    // action row: copy / paste / delete
    const actions = document.createElement("div");
    Object.assign(actions.style, { display: "flex", gap: "6px", marginTop: "2px" } as CSSStyleDeclaration);
    const mkAction = (label: string, fn: () => void) => {
      const b = document.createElement("button");
      b.textContent = label;
      Object.assign(b.style, {
        flex: "1",
        padding: "5px 6px",
        background: "#1b2129",
        border: "1px solid #2a323c",
        borderRadius: "6px",
        color: "#cdd6e0",
        cursor: "pointer",
      } as CSSStyleDeclaration);
      b.addEventListener("click", fn);
      actions.appendChild(b);
    };
    mkAction("Copy", () => this.copySelection());
    mkAction("Paste", () => this.paste());
    mkAction("Undo", () => this.undo());
    mkAction("Del", () => this.deleteSelection());
    mkAction("Clear", () => this.clear());
    bar.appendChild(actions);

    const mirrorBtn = document.createElement("button");
    mirrorBtn.type = "button";
    mirrorBtn.textContent = "Mirror On";
    mirrorBtn.title = "Mirror new nodes and members across the centerline (M)";
    Object.assign(mirrorBtn.style, {
      padding: "5px 6px",
      background: "#123047",
      border: "1px solid #1d5c86",
      borderRadius: "6px",
      color: "#cfe8ff",
      cursor: "pointer",
    } as CSSStyleDeclaration);
    mirrorBtn.addEventListener("click", () => {
      this.mirrorMode = !this.mirrorMode;
      this.refreshMirrorButton();
      this.updateHint();
    });
    this.mirrorButton = mirrorBtn;
    bar.appendChild(mirrorBtn);

    const repeat = document.createElement("div");
    Object.assign(repeat.style, {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "6px",
      marginTop: "2px",
      paddingTop: "6px",
      borderTop: "1px solid #2a323c",
    } as CSSStyleDeclaration);

    const mkNumber = (label: string, value: string, title: string) => {
      const wrap = document.createElement("label");
      Object.assign(wrap.style, {
        display: "grid",
        gap: "2px",
        color: "#7f8b99",
        fontSize: "11px",
      } as CSSStyleDeclaration);
      const input = document.createElement("input");
      input.type = "number";
      input.value = value;
      input.step = "0.05";
      input.min = "0.05";
      input.title = title;
      Object.assign(input.style, {
        width: "100%",
        boxSizing: "border-box",
        padding: "4px 5px",
        background: "#111823",
        border: "1px solid #2a323c",
        borderRadius: "6px",
        color: "#cdd6e0",
        font: "12px system-ui, sans-serif",
      } as CSSStyleDeclaration);
      wrap.append(label, input);
      repeat.appendChild(wrap);
      return input;
    };

    this.repeatRiseInput = mkNumber("Rise m", "5", "Vertical distance for Repeat Up");
    this.repeatScaleInput = mkNumber("Top scale", "0.9", "Width multiplier for the new top row");
    const repeatBtn = document.createElement("button");
    repeatBtn.textContent = "Repeat Up";
    repeatBtn.title = "Copy the selected pattern upward, sharing the previous top row";
    Object.assign(repeatBtn.style, {
      gridColumn: "1 / -1",
      padding: "5px 6px",
      background: "#123047",
      border: "1px solid #1d5c86",
      borderRadius: "6px",
      color: "#cfe8ff",
      cursor: "pointer",
    } as CSSStyleDeclaration);
    repeatBtn.addEventListener("click", () => this.repeatSelection());
    repeat.appendChild(repeatBtn);
    bar.appendChild(repeat);

    const hint = document.createElement("div");
    Object.assign(hint.style, {
      marginTop: "2px",
      maxWidth: "180px",
      color: "#7f8b99",
    } as CSSStyleDeclaration);
    this.hint = hint;
    bar.appendChild(hint);

    (this.toolbarHost ?? this.opts.container).appendChild(bar);
    this.toolbar = bar;
    this.refreshModeControls();
    this.refreshPaletteButtons();
    this.refreshMirrorButton();
    this.updateHint();
  }

  private refreshPaletteButtons(): void {
    const selectedSection = this.selectedMemberSection();
    const showPalette = this.interactionMode === "draw" || this.selMembers.size > 0;
    this.paletteButtons.forEach((btn, i) => {
      const item = PALETTE[i]!;
      const on = this.interactionMode === "draw" ? item === this.active : item.key === selectedSection;
      btn.style.display = showPalette ? "flex" : "none";
      btn.title =
        this.interactionMode === "draw"
          ? `Draw ${item.label} (${i + 1})`
          : `Change selected members to ${item.label} (${i + 1})`;
      btn.style.borderColor = on ? "#" + C_SEL.toString(16) : "#2a323c";
      btn.style.background = on ? "#243040" : "#1b2129";
    });
    this.refreshMaterialSelect();
    this.refreshSectionSelect();
  }

  private selectedMemberSection(): SectionKey | null {
    let section: SectionKey | null = null;
    for (const m of this.members) {
      if (!this.selMembers.has(m.id)) continue;
      if (section === null) section = m.section;
      else if (section !== m.section) return null;
    }
    return section;
  }

  private refreshModeControls(): void {
    this.modeButtons.forEach((btn) => {
      const on = btn.dataset.mode === this.interactionMode;
      btn.style.borderColor = on ? "#ffe65c" : "#2a323c";
      btn.style.background = on ? "#352d11" : "#1b2129";
      btn.style.color = on ? "#fff1a8" : "#cdd6e0";
    });
    if (!this.modeIndicator) return;
    const drawing = this.interactionMode === "draw";
    this.modeIndicator.textContent = drawing ? "DRAWING MODE" : "EDIT MODE";
    this.modeIndicator.style.background = drawing ? "#352d11" : "#10251d";
    this.modeIndicator.style.borderColor = drawing ? "#8f7d24" : "#1d6b4f";
    this.modeIndicator.style.color = drawing ? "#fff1a8" : "#b7f7d5";
  }

  private refreshMirrorButton(): void {
    if (!this.mirrorButton) return;
    this.mirrorButton.textContent = this.mirrorMode ? "Mirror On" : "Mirror Off";
    this.mirrorButton.style.background = this.mirrorMode ? "#123047" : "#1b2129";
    this.mirrorButton.style.borderColor = this.mirrorMode ? "#1d5c86" : "#2a323c";
  }

  private updateHint(): void {
    if (!this.hint) return;
    const nSel = this.selNodes.size;
    const mSel = this.selMembers.size;
    const mode = this.interactionMode === "draw" ? "Drawing Mode" : "Edit Mode";
    this.hint.textContent =
      nSel + mSel > 0
        ? `${mode}. Selected ${nSel} node(s), ${mSel} member(s). Drag to move, R repeats up.`
        : this.interactionMode === "draw"
          ? `Drawing Mode. Drag to add the active member. ${
              this.mirrorMode ? "Mirror is on." : "Mirror is off."
            }`
          : "Edit Mode. Click to select; drag any joint or line to move it; Del removes it.";
  }

  private makeToolbarDraggable(bar: HTMLElement): void {
    let startX = 0;
    let startY = 0;
    let baseLeft = 0;
    let baseTop = 0;
    const onMove = (e: PointerEvent) => {
      const w = bar.offsetWidth;
      const h = bar.offsetHeight;
      const left = Math.max(0, Math.min(window.innerWidth - Math.min(w, 80), baseLeft + e.clientX - startX));
      const top = Math.max(0, Math.min(window.innerHeight - Math.min(h, 40), baseTop + e.clientY - startY));
      bar.style.left = `${left}px`;
      bar.style.top = `${top}px`;
      bar.style.right = "auto";
      bar.style.bottom = "auto";
    };
    const onUp = (e: PointerEvent) => {
      bar.style.cursor = "grab";
      bar.releasePointerCapture?.(e.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    bar.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest("button,input,select,textarea")) return;
      const r = bar.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      baseLeft = r.left;
      baseTop = r.top;
      bar.style.cursor = "grabbing";
      bar.setPointerCapture?.(e.pointerId);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      e.preventDefault();
      e.stopPropagation();
    });
  }
}

// ── free helpers ─────────────────────────────────────────────────────────────

function paletteFor(key: SectionKey): PaletteItem {
  for (const p of PALETTE) if (p.key === key) return p;
  return PALETTE[0]!;
}

function isGuyKey(key: SectionKey): boolean {
  const p = PALETTE.find((q) => q.key === key);
  if (p) return p.guy;
  return String(key).startsWith("guy");
}

function sectionKeyForMember(member: Member): SectionKey {
  for (const [key, section] of Object.entries(SECTIONS) as Array<[SectionKey, Section]>) {
    if (section === member.section || section.name === member.section.name) return key;
  }
  return member.tensionOnly ? "guy14" : "braceMed";
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function segPointDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const vx = bx - ax;
  const vy = by - ay;
  const wx = px - ax;
  const wy = py - ay;
  const len2 = vx * vx + vy * vy;
  let t = len2 > 0 ? (wx * vx + wy * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * vx;
  const cy = ay + t * vy;
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy;
}

function segmentIntersection(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): { x: number; y: number; t: number } | null {
  const rx = bx - ax;
  const ry = by - ay;
  const sx = dx - cx;
  const sy = dy - cy;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qpx = cx - ax;
  const qpy = cy - ay;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  const eps = 1e-4;
  if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return null;
  return { x: ax + t * rx, y: ay + t * ry, t };
}

function pointInRect(x: number, y: number, lox: number, loy: number, hix: number, hiy: number): boolean {
  return x >= lox && x <= hix && y >= loy && y <= hiy;
}

function segmentIntersectsRect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  lox: number,
  loy: number,
  hix: number,
  hiy: number,
): boolean {
  if (pointInRect(ax, ay, lox, loy, hix, hiy) || pointInRect(bx, by, lox, loy, hix, hiy)) return true;
  return (
    segmentIntersection(ax, ay, bx, by, lox, loy, hix, loy) !== null ||
    segmentIntersection(ax, ay, bx, by, hix, loy, hix, hiy) !== null ||
    segmentIntersection(ax, ay, bx, by, hix, hiy, lox, hiy) !== null ||
    segmentIntersection(ax, ay, bx, by, lox, hiy, lox, loy) !== null
  );
}
